import { useEffect, useState } from 'react';

import { useRouteLoaderData } from '@remix-run/react';
import type { NavbarSerializableNode, TNavbarDropdownElement, TNavbarItemElement } from '@shared/dream-components';
import {
  applyThemeToNavbarNode,
  filterRows,
  getNavbarUserButtonContent,
  reorganizeNavbarMobileChildren,
} from '@shared/dream-components';

import type { Site } from '~/interfaces/site';
import type { RootLoaderData } from '~/root';
import isUserAgentMobile from '~/utils/isUserAgentMobile';

import NavbarRenderer from './NavbarRenderer';

interface NavbarSerializerProps {
  site: Site;
}

interface NavbarSerializerRenderProps {
  data: NavbarSerializableNode;
  isMobile: boolean;
}

declare global {
  interface Window {
    opera: any;
  }

  interface Navigator {
    userAgentData: {
      mobile: boolean;
    };
  }
}

const isMobileDevice = () => {
  if (typeof window === 'undefined') {
    return false;
  }

  if (isUserAgentMobile(navigator.userAgent || navigator.vendor || window.opera)) {
    return true;
  }

  if (typeof navigator !== 'undefined' && navigator?.userAgentData?.mobile) {
    return true;
  }

  return (window?.innerWidth || 1024) < 768;
};

// Custom hook for detecting mobile viewport
const useIsMobile = (defaultValue: boolean) => {
  const [isMobile, setIsMobile] = useState<boolean>(defaultValue);

  useEffect(() => {
    setIsMobile(isMobileDevice());

    window?.addEventListener('resize', () => {
      setIsMobile(isMobileDevice());
    });
    return () =>
      window?.removeEventListener('resize', () => {
        setIsMobile(isMobileDevice());
      });
  }, []);

  return isMobile;
};

const serializeNode = (node: NavbarSerializableNode | NavbarSerializableNode[], screen: 'desktop' | 'mobile') => {
  if (!node) {
    return null;
  }

  if (Array.isArray(node)) {
    return node.map((node: NavbarSerializableNode, index: number) => (
      <NavbarRenderer key={index} node={node} screen={screen}>
        {'content' in node && node.content ? serializeNode(node.content as NavbarSerializableNode[], screen) : null}
      </NavbarRenderer>
    ));
  }

  return (
    <NavbarRenderer node={node} screen={screen}>
      {'content' in node && node.content ? serializeNode(node.content as NavbarSerializableNode[], screen) : null}
    </NavbarRenderer>
  );
};
const NavbarSerializerRender = ({ data, isMobile }: NavbarSerializerRenderProps) => {
  return serializeNode(data as NavbarSerializableNode | NavbarSerializableNode[], isMobile ? 'mobile' : 'desktop');
};

const applyUserButtonDropdown = (node: NavbarSerializableNode, site: Site) => {
  if (Array.isArray(node)) {
    node.forEach((childNode) => {
      applyUserButtonDropdown(childNode, site);
    });
    return;
  }

  // Handle User Button Dropdown Defaults
  if (
    node.attrs &&
    'type' in node.attrs &&
    node.attrs?.type === 'user_button' &&
    node.attrs?.action === 'profile' &&
    node.attrs?.style !== 'button'
  ) {
    // Only convert to dropdown if we have a theme and primary theme
    if (site?.theme_rules && site?.primary_theme) {
      node.attrs.style = 'dropdown';
      node.attrs.href = undefined;
      node.attrs.showWhen = 'all_logged_in';
      if (!('content' in node) || !node.content || node.content.length === 0) {
        let newContent = getNavbarUserButtonContent();
        newContent = applyThemeToNavbarNode(
          newContent,
          site?.primary_theme,
          site?.theme_rules,
        ) as TNavbarDropdownElement;
        (node as TNavbarItemElement).content = [newContent];
      }
    } else if (node.attrs?.style === undefined) {
      // If we don't have a theme and the style is undefined, set it to button
      node.attrs.style = 'button';
      node.attrs.href = undefined;
      node.attrs.showWhen = 'all_logged_in';
    }
  }

  // Recursively check children
  if ('content' in node && node.content && Array.isArray(node.content)) {
    node.content.forEach((childNode) => {
      applyUserButtonDropdown(childNode, site);
    });
  }
};

const NavbarSerializer = ({ site }: NavbarSerializerProps) => {
  const { isUAStringMobile } = useRouteLoaderData('root') as RootLoaderData;
  const navbarData = site.viewable_site_version.navbar;

  const isMobile = useIsMobile(isUAStringMobile);

  if (!navbarData || isMobile === undefined) {
    return null;
  }

  const data = reorganizeNavbarMobileChildren(filterRows(navbarData as NavbarSerializableNode));
  applyUserButtonDropdown(data, site);

  return <NavbarSerializerRender isMobile={isMobile} data={data} />;
};

export default NavbarSerializer;
